import React, { useMemo } from 'react';
import { ChevronLeft, ChevronRight, CalendarDays } from 'lucide-react';
import type { CommonArea, CommonAreaBooking, BookingStatus } from '../../services/commonAreaReservationService';
import { cn } from '../../utils/cn';
import { calendarMonthDays } from './reservaRules';
import { bookingDateISO, bookingHoursLabel, bookingChipTimeLabel } from './bookingLabels';
import { bookingStatusChip, bookingStatusLabel } from './bookingStatusStyles';

/**
 * The month, for the portaria.
 *
 * Agendamentos was a flat list behind a from/to date pair, so answering "is the salão free on the
 * 24th?" meant retyping a range and reading rows. The síndico apps have had a month view for
 * exactly this question (SindicoReservasCalendar); this is the web's.
 *
 * Days are keyed off the booking's OWN calendar date (bookingDateISO) rather than a Date built
 * from the timestamp: a whole-day reservation is stored as 00:00Z, which in UTC-3 reads as the
 * previous day and would file the reservation under the wrong cell.
 */

export interface BookingsCalendarProps {
  bookings: CommonAreaBooking[];
  areas: CommonArea[];
  /** First day of the displayed month. Controlled, so the parent can fetch that range. */
  monthAnchor: Date;
  onMonthChange: (anchor: Date) => void;
  onSelectBooking: (booking: CommonAreaBooking) => void;
  lightTheme?: boolean;
  /** Injected by tests so "hoje" is deterministic. */
  now?: Date;
}

const WEEKDAYS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
const MONTHS = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
];

/** How many fit in a cell before folding — beyond this the row grows and the month stops fitting. */
const PER_DAY = 3;

const personName = (b: CommonAreaBooking): string => {
  const p: any = (b as any).person;
  const full = [p?.first_name, p?.last_name].filter(Boolean).join(' ').trim();
  return full || 'Morador';
};

const localDateString = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const BookingsCalendar: React.FC<BookingsCalendarProps> = ({
  bookings,
  areas,
  monthAnchor,
  onMonthChange,
  onSelectBooking,
  lightTheme = true,
  now,
}) => {
  const today = useMemo(() => now ?? new Date(), [now]);
  const todayISO = localDateString(today);

  const days = useMemo(
    () => calendarMonthDays(monthAnchor.getFullYear(), monthAnchor.getMonth()),
    [monthAnchor],
  );

  const byDay = useMemo(() => {
    const map = new Map<string, CommonAreaBooking[]>();
    for (const b of bookings) {
      const day = bookingDateISO(b);
      if (!day) continue;
      const list = map.get(day);
      if (list) list.push(b);
      else map.set(day, [b]);
    }
    // forEach, not `for...of map.values()`: the project's tsconfig targets below ES2015 for
    // iterables, and that form fails to compile (TS2802).
    map.forEach(list => list.sort((a, b) => a.start_time.localeCompare(b.start_time)));
    return map;
  }, [bookings]);

  const monthHasAny = useMemo(
    () => days.some(d => d.inMonth && (byDay.get(d.dateISO)?.length ?? 0) > 0),
    [days, byDay],
  );

  const text = lightTheme ? 'text-gray-900' : 'text-white';
  const subtext = lightTheme ? 'text-gray-500' : 'text-gray-400';
  const borderCls = lightTheme ? 'border-gray-200' : 'border-gray-800';
  const cellBg = lightTheme ? 'bg-white' : 'bg-gray-900/40';
  const outsideBg = lightTheme ? 'bg-gray-50' : 'bg-gray-950/60';
  const navBtn = cn('p-1.5 rounded-lg transition-colors',
    lightTheme ? 'hover:bg-gray-100 text-gray-600' : 'hover:bg-gray-800 text-gray-300');

  const chipHover = lightTheme ? 'hover:bg-gray-200' : 'hover:bg-gray-700';

  return (
    <div className="flex flex-col min-h-0 flex-1">
      {/* Month nav */}
      <div className={cn('flex-shrink-0 flex items-center justify-between px-4 py-2 border-b', borderCls,
        lightTheme ? 'bg-gray-50' : 'bg-gray-900/50')}>
        <div className="flex items-center gap-1">
          <button
            type="button"
            aria-label="Mês anterior"
            onClick={() => onMonthChange(new Date(monthAnchor.getFullYear(), monthAnchor.getMonth() - 1, 1))}
            className={navBtn}
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button
            type="button"
            aria-label="Próximo mês"
            onClick={() => onMonthChange(new Date(monthAnchor.getFullYear(), monthAnchor.getMonth() + 1, 1))}
            className={navBtn}
          >
            <ChevronRight className="h-4 w-4" />
          </button>
          <span className={cn('ml-2 text-sm font-bold', text)}>
            {MONTHS[monthAnchor.getMonth()]} de {monthAnchor.getFullYear()}
          </span>
        </div>
        <button
          type="button"
          onClick={() => onMonthChange(new Date(today.getFullYear(), today.getMonth(), 1))}
          className={cn('px-2.5 py-1 rounded-lg text-xs font-semibold border transition-colors',
            lightTheme ? 'border-gray-300 text-gray-600 hover:bg-gray-100' : 'border-gray-700 text-gray-400 hover:bg-gray-800')}
        >
          Hoje
        </button>
      </div>

      {/* Weekday header */}
      <div className={cn('flex-shrink-0 grid grid-cols-7 border-b', borderCls)}>
        {WEEKDAYS.map(w => (
          <div key={w} className={cn('px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-center', subtext)}>
            {w}
          </div>
        ))}
      </div>

      {/* Grid */}
      <div className="flex-1 overflow-y-auto">
        {!monthHasAny && (
          <div className={cn('flex items-center justify-center gap-2 py-3 text-sm border-b', subtext, borderCls)}>
            <CalendarDays className="h-4 w-4" />
            Nenhuma reserva neste mês
          </div>
        )}
        <div className="grid grid-cols-7">
          {days.map(day => {
            const dayBookings = byDay.get(day.dateISO) ?? [];
            const visible = dayBookings.slice(0, PER_DAY);
            const folded = dayBookings.length - visible.length;
            const isToday = day.dateISO === todayISO;
            return (
              <div
                key={day.dateISO}
                data-testid={`day-${day.dateISO}`}
                data-today={String(isToday)}
                className={cn('min-h-[88px] border-b border-r p-1 space-y-0.5', borderCls,
                  day.inMonth ? cellBg : outsideBg)}
              >
                <div className="flex items-center justify-between">
                  <span className={cn('inline-flex items-center justify-center h-5 w-5 rounded-full text-[11px] font-semibold',
                    isToday ? 'bg-blue-600 text-white' : day.inMonth ? text : subtext)}>
                    {Number(day.dateISO.slice(8, 10))}
                  </span>
                </div>

                {visible.map(b => {
                  const area = areas.find(a => a.id === b.common_area_id) ?? null;
                  const status = b.status as BookingStatus;
                  const chip = bookingStatusChip(status, lightTheme);
                  const dead = status === 'Cancelled' || status === 'Revoked';
                  return (
                    <button
                      key={b.id}
                      type="button"
                      onClick={() => onSelectBooking(b)}
                      title={`${personName(b)} · ${area?.name ?? 'Área'} · ${bookingHoursLabel(b, area)} · ${bookingStatusLabel(status)}`}
                      className={cn('w-full flex items-center gap-1 px-1 py-0.5 rounded border text-[10px] leading-tight text-left truncate transition-colors',
                        chip.chip, chipHover, dead && 'line-through')}
                    >
                      {/* The dot is what says Pendente/Aprovado here — a chip tinted the status
                          colour put coloured text on a coloured fill, which is unreadable at
                          10px on the white theme. */}
                      <span className={cn('h-1.5 w-1.5 rounded-full flex-shrink-0', chip.dot)} />
                      <span className="font-semibold flex-shrink-0">{bookingChipTimeLabel(b, area)}</span>
                      <span className="truncate">{personName(b)}</span>
                    </button>
                  );
                })}

                {folded > 0 && (
                  <p className={cn('text-[10px] px-1', subtext)}>+{folded} mais</p>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default BookingsCalendar;
